"use client";
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "lib/utils";
import { Loader2, RefreshCw, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Checkbox } from "components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import { routes } from "router";
import { signUpAction } from "action/auth-action";
import { checkDomainRequest, addressSuggestionsRequest, addressDetailsRequest } from "queries/auth-queries";
import { useAuth } from "context/auth-context";
import { Icon } from "components/ui/icon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { BUSINESS_CATEGORIES } from "lib/business-categories";

// Schema
const schema = z.object({
    businessName: z.string().min(2, { message: "Business name is required" }),
    businessCategory: z.string().refine((value) => BUSINESS_CATEGORIES.some((c) => c.value === value), {
        message: "Choose what your business sells",
    }),
    phone1: z.string().min(10, { message: "Phone number is required" }),
    phone2: z.string().optional(),
    email: z.string().email({ message: "Invalid email address" }),
    address: z.string().min(5, { message: "Address is required" }),
    domainName: z.string()
        .min(3, { message: "Domain name must be at least 3 characters" })
        .transform(val => val.toLowerCase().replace(".storbolt.com", ""))
        .refine(val => /^[a-z0-9-]+$/.test(val), {
            message: "Domain prefix must contain only lowercase letters, numbers, and hyphens"
        }),
    password: z.string().min(6, { message: "Password must be at least 6 characters" }),
    agreeTerms: z.literal(true, { errorMap: () => ({ message: "You must agree to the terms" }) }),
});

const SignUpForm = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = React.useState(false);
    const [checkingDomain, setCheckingDomain] = React.useState(false);
    const [domainAvailable, setDomainAvailable] = React.useState<boolean | null>(null);
    const [passwordType, setPasswordType] = React.useState("password");
    const [predictions, setPredictions] = React.useState<any[]>([]);
    const [addressCoords, setAddressCoords] = React.useState<string | null>(null);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        trigger
    } = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        mode: "all",
        defaultValues: {
            businessName: "",
            businessCategory: "",
            phone1: "",
            phone2: "",
            email: "",
            address: "",
            domainName: "",
            password: "",
            agreeTerms: true,
        },
    });

    const businessName = watch("businessName");
    const domainName = watch("domainName");

    // Domain generator and checker
    const checkDomain = React.useCallback(async (name: string, generate: boolean = false) => {
        if (!name && !generate) return;
        setCheckingDomain(true);
        try {
            const query = generate
                ? `?generate=true&businessName=${encodeURIComponent(businessName)}`
                : `?domain=${encodeURIComponent(name)}`;

            const response = await checkDomainRequest(query);
            if (response.ok) {
                const data = await response.json();
                if (generate) {
                    setValue("domainName", data.domain);
                    trigger("domainName");
                }
                setDomainAvailable(data.available);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setCheckingDomain(false);
        }
    }, [businessName, setValue, trigger]);

    // Auto-generate domain when business name reaches a certain length and domain is empty
    React.useEffect(() => {
        const timer = setTimeout(() => {
            if (businessName.length > 3 && !domainName) {
                checkDomain("", true);
            }
        }, 1000);
        return () => clearTimeout(timer);
    }, [businessName, domainName, checkDomain]);

    // Check availability when domain changes manually
    React.useEffect(() => {
        const timer = setTimeout(() => {
            if (domainName) {
                checkDomain(domainName, false);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [domainName, checkDomain]);

    // Address autocomplete logic with debounce to protect API from abuse
    const address = watch("address");
    React.useEffect(() => {
        if (!address || address.length < 3) {
            setPredictions([]);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                const response = await addressSuggestionsRequest(address);
                const data = await response.json();
                if (data.status === "OK") {
                    setPredictions(data.predictions);
                }
            } catch (error) {
                console.error("Address Suggestions Error", error);
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timer);
    }, [address]);

    const selectAddress = async (prediction: any) => {
        setValue("address", prediction.description);
        setPredictions([]);

        try {
            const response = await addressDetailsRequest(prediction.description, prediction.place_id);
            const data = await response.json();
            if (data.status === "OK") {
                const location = data.results?.[0]?.geometry?.location;
                if (!location) {
                    return;
                }
                // Format: POINT(lng lat)
                const point = `POINT(${location.lng} ${location.lat})`;
                setAddressCoords(point);
            }
        } catch (error) {
            console.error("Address Details Error", error);
        }
    };

    const onSubmit = (data: z.infer<typeof schema>) => {
        // Honeypot check (simple bot protection)
        if ((data as any).website) {
            // console.warn("Honeypot hit - ignoring submission");
            return;
        }

        if (!addressCoords) {
            toast.error("Please select a valid address from the suggestions");
            return;
        }

        setLoading(true);
        const payload = { ...data, coordinates: addressCoords };

        signUpAction(payload)
            .then((response) => {
                if (response.status === 201) {
                    const businessInfo = {
                        businessId: response.body.business_id,
                        businessDomain: response.body.business_domain,
                        businessName: response.body.business_name,
                    };
                    React.startTransition(() => {
                        login(
                            response.body.access_token,
                            response.body.refresh_token,
                            businessInfo
                        );
                        navigate(routes.Home.path, { replace: true });
                    });
                    toast.success("Account created successfully");
                } else {
                    setLoading(false);
                    toast.error(response.error);
                }
            })
            .catch((err) => {
                setLoading(false);
                toast.error("Failed to sign up");
                console.error(err);
            });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 2xl:mt-7 space-y-4">
            {/* Honeypot field (hidden from users) */}
            <Input type="text" {...register("website" as any)} className="hidden" tabIndex={-1} autoComplete="off" />
            {/* Business Name */}
            <div className="space-y-2">
                <Label htmlFor="businessName">Business Name <span className="text-destructive">*</span></Label>
                <Input size="lg" id="businessName" {...register("businessName")} placeholder="e.g. My Awesome Store" className={cn({ "border-destructive": errors.businessName })} />
                {errors.businessName && <div className="text-destructive text-sm">{errors.businessName.message}</div>}
            </div>

            {/* Business Category */}
            <div className="space-y-2">
                <Label htmlFor="businessCategory">Business Category <span className="text-destructive">*</span></Label>
                <Select
                    value={watch("businessCategory") || undefined}
                    onValueChange={(value) => setValue("businessCategory", value, { shouldValidate: true })}
                >
                    <SelectTrigger id="businessCategory" size="lg" className={cn({ "border-destructive": errors.businessCategory })}>
                        <SelectValue placeholder="What does your business sell?" />
                    </SelectTrigger>
                    <SelectContent>
                        {BUSINESS_CATEGORIES.map((category) => (
                            <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.businessCategory && <div className="text-destructive text-sm">{errors.businessCategory.message}</div>}
            </div>

            {/* Domain Name */}
            <div className="space-y-2">
                <Label htmlFor="domainName">Store Domain <span className="text-destructive">*</span></Label>
                <div className="relative">
                    <Input size="lg" id="domainName" {...register("domainName")} className={cn("pr-20", { "border-destructive": errors.domainName })} />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                        {checkingDomain && <Loader2 className="w-4 h-4 animate-spin text-default-400" />}
                        {!checkingDomain && domainAvailable === true && <Check className="w-4 h-4 text-success" />}
                        {!checkingDomain && domainAvailable === false && <X className="w-4 h-4 text-destructive" />}
                        <Button type="button" size="icon" variant="ghost" className="h-6 w-6" onClick={() => checkDomain("", true)}>
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
                <div className="text-xs text-default-500">Your store will be at: {watch("domainName") ? (watch("domainName").endsWith(".storbolt.com") ? watch("domainName") : `${watch("domainName")}.storbolt.com`) : "..."}</div>
                {errors.domainName && <div className="text-destructive text-sm">{errors.domainName.message}</div>}
            </div>

            {/* Email */}
            <div className="space-y-2">
                <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                <Input size="lg" id="email" type="email" {...register("email")} className={cn({ "border-destructive": errors.email })} />
                {errors.email && <div className="text-destructive text-sm">{errors.email.message}</div>}
            </div>

            {/* Phones */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="phone1">Phone 1 <span className="text-destructive">*</span></Label>
                    <Input size="lg" id="phone1" {...register("phone1")} className={cn({ "border-destructive": errors.phone1 })} />
                    {errors.phone1 && <div className="text-destructive text-sm">{errors.phone1.message}</div>}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="phone2">Phone 2 (Optional)</Label>
                    <Input size="lg" id="phone2" {...register("phone2")} />
                </div>
            </div>

            {/* Address with Autocomplete */}
            <div className="space-y-2 relative">
                <Label htmlFor="address">Address <span className="text-destructive">*</span></Label>
                <Input
                    size="lg"
                    id="address"
                    {...register("address")}
                    autoComplete="off"
                    className={cn({ "border-destructive": errors.address })}
                />
                {predictions.length > 0 && (
                    <ul className="absolute z-10 w-full bg-popover border border-default-200 rounded-lg shadow-lg max-h-60 overflow-auto mt-1">
                        {predictions.map((p) => (
                            <li key={p.place_id} className="p-2 hover:bg-default-50 cursor-pointer text-sm" onClick={() => selectAddress(p)}>
                                {p.description}
                            </li>
                        ))}
                    </ul>
                )}
                {errors.address && <div className="text-destructive text-sm">{errors.address.message}</div>}
            </div>

            {/* Password */}
            <div className="space-y-2">
                <Label htmlFor="password">Password <span className="text-destructive">*</span></Label>
                <div className="relative">
                    <Input size="lg" id="password" type={passwordType} {...register("password")} className={cn({ "border-destructive": errors.password })} />
                    <div className="absolute top-1/2 -translate-y-1/2 right-2 cursor-pointer" onClick={() => setPasswordType(prev => prev === "password" ? "text" : "password")}>
                        {passwordType === "password" ? <Icon icon="heroicons:eye" className="w-5 h-5 text-default-400" /> : <Icon icon="heroicons:eye-slash" className="w-5 h-5 text-default-400" />}
                    </div>
                </div>
                {errors.password && <div className="text-destructive text-sm">{errors.password.message}</div>}
            </div>

            <div className="flex gap-2 items-center">
                <Checkbox id="agreeTerms" defaultChecked={true} {...register("agreeTerms")} onCheckedChange={(c) => setValue("agreeTerms", c === true ? true : false as any)} />
                <Label htmlFor="agreeTerms" className="text-sm text-default-500">I agree to the Terms and Conditions</Label>
            </div>
            {errors.agreeTerms && <div className="text-destructive text-sm">{errors.agreeTerms.message}</div>}

            <Button fullWidth disabled={loading} size="lg">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {loading ? "Creating Account..." : "Sign Up"}
            </Button>
        </form>
    );
};

export default SignUpForm;
